import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

interface ImageRefContextValue {
  imgRef: React.RefObject<HTMLImageElement>;
  imgSize: { width: number; height: number } | null;
  setImgSize: (size: { width: number; height: number } | null) => void;
  setImgRef: (node: HTMLImageElement | null) => void;
  /** Rendered (client) dimensions of the <img> element, kept in sync via ResizeObserver. */
  renderedSize: { width: number; height: number } | null;
}

const ImageRefContext = createContext<ImageRefContextValue | null>(null);

export const useImageRef = (): ImageRefContextValue => {
  const ctx = useContext(ImageRefContext);
  if (!ctx) throw new Error('useImageRef must be used within an ImageRefProvider');
  return ctx;
};

export const ImageRefProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const [imgSize, setImgSize] = useState<{ width: number; height: number } | null>(null);
  const [renderedSize, setRenderedSize] = useState<{ width: number; height: number } | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const setImgRefCallback = useCallback((node: HTMLImageElement | null) => {
    if (imgRef) {
      (imgRef as React.MutableRefObject<HTMLImageElement | null>).current = node;
    }

    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }

    if (node) {
      setRenderedSize({ width: node.clientWidth, height: node.clientHeight });
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserverRef.current = new ResizeObserver((entries) => {
          const entry = entries[0];
          if (entry) {
            const { width, height } = entry.contentRect;
            setRenderedSize({ width, height });
          }
        });
        resizeObserverRef.current.observe(node);
      }
    } else {
      setRenderedSize(null);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, []);

  return (
    <ImageRefContext.Provider value={{ imgRef, imgSize, setImgSize, setImgRef: setImgRefCallback, renderedSize }}>
      {children}
    </ImageRefContext.Provider>
  );
};
